import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import './TreasuryStrategy.css';
import './AIAutomation.css';

/* Line-drawing SVG icons for engagement phases */
const PhaseIcon = ({ type }) => {
  const icons = {
    assess: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="#1B4620" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="14" cy="14" r="9" />
        <line x1="20.5" y1="20.5" x2="27" y2="27" />
        <line x1="14" y1="10" x2="14" y2="18" />
        <line x1="10" y1="14" x2="18" y2="14" />
      </svg>
    ),
    design: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="#1B4620" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="4" width="24" height="24" rx="3" />
        <line x1="4" y1="12" x2="28" y2="12" />
        <line x1="12" y1="12" x2="12" y2="28" />
        <rect x="16" y="16" width="8" height="4" rx="1" />
        <rect x="16" y="23" width="5" height="2" rx="1" />
      </svg>
    ),
    implement: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="#1B4620" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="8,16 13,21 24,10" />
        <circle cx="16" cy="16" r="13" />
      </svg>
    ),
  };
  return icons[type] || null;
};

const phases = [
  {
    iconType: 'assess',
    label: 'Assess',
    desc: 'Map current operations, identify where time is lost, prioritize high-value opportunities',
  },
  {
    iconType: 'design',
    label: 'Design',
    desc: 'Select tools, define data flows, establish guardrails, design workflows that fit your team',
  },
  {
    iconType: 'implement',
    label: 'Implement',
    desc: 'Deploy, test, and train your team. We stay through adoption, not just delivery',
  },
];

const AIAutomation = () => {
  return (
    <>
      <Navbar />
      <main className="treasury-page ai-page" style={{ paddingTop: '80px' }}>

        {/* ── Hub & Spoke: How Engagements Work ── */}
        <section className="hub-section ai-hub-section">
          <div className="hub-container">
            <div className="treasury-page-header ai-header" style={{ marginBottom: '1.25rem' }}>
              <Link to="/" className="treasury-back">&larr; Home</Link>
              <h1>AI &amp; Lending Operations</h1>
              <p className="ai-hook">
                Half of your team is already using AI. The other half doesn't know it.
                We help you move from ad hoc to institutional.
              </p>
            </div>

            <h2 className="hub-section-title">How Engagements Work</h2>

            <div className="hub-wrapper ai-hub-wrapper">
              {/* Three spokes in a row */}
              <div className="hub-row ai-hub-row">
                {phases.map((phase, i) => (
                  <div key={i} className="spoke-item ai-spoke-item">
                    <div className="spoke-icon"><PhaseIcon type={phase.iconType} /></div>
                    <div className="spoke-label">{phase.label}</div>
                    <div className="spoke-desc">{phase.desc}</div>
                  </div>
                ))}
              </div>

              {/* Arrows pointing down */}
              <div className="hub-arrows">
                <span>&#9660;</span><span>&#9660;</span><span>&#9660;</span>
              </div>

              {/* Center hub */}
              <div className="hub-center ai-hub-center">
                <div className="hub-center-inner">
                  <span>Your Lending</span>
                  <span>Operation</span>
                </div>
              </div>
            </div>

            <p className="ai-arc-note" style={{ textAlign: 'center', marginTop: '0.75rem' }}>
              Full arc or any single phase. Ongoing support available.
            </p>
          </div>
        </section>

        {/* ── Two-column: Lifecycle + Sidebar ── */}
        <section className="treasury-main-section">
          <div className="treasury-container-wide">

            {/* LEFT COLUMN */}
            <div className="treasury-col-flow">
              <h2 className="ai-lifecycle-heading">Across the Lending Lifecycle</h2>

              <div className="flow ai-flow">
                <div className="flow-step">
                  <div className="ai-stage-dot"></div>
                  <div className="flow-card ai-card">
                    <h3>Intake &amp; Document Management</h3>
                    <p>
                      Automated document classification, completeness checks, and
                      borrower communication so your team stops chasing missing
                      items and starts underwriting sooner.
                    </p>
                  </div>
                </div>

                <div className="flow-step">
                  <div className="ai-stage-dot"></div>
                  <div className="flow-card ai-card">
                    <h3>Underwriting &amp; Credit Analysis</h3>
                    <p>
                      Financial spreading, credit memo drafting, and data validation.
                      Your underwriter reviews a structured first draft, not a blank page.
                    </p>
                  </div>
                </div>

                <div className="flow-step">
                  <div className="ai-stage-dot"></div>
                  <div className="flow-card ai-card">
                    <h3>Closing &amp; Workflow</h3>
                    <p>
                      Task routing, closing checklists by deal type, and document
                      tracking. Your team knows what's outstanding without asking.
                    </p>
                  </div>
                </div>

                <div className="flow-step">
                  <div className="ai-stage-dot"></div>
                  <div className="flow-card ai-card">
                    <h3>Portfolio Monitoring</h3>
                    <p>
                      Covenant tracking, financial statement ingestion, and early
                      warning alerts. Clean data into your reporting workflows
                      instead of rekeying from borrower PDFs every quarter.
                    </p>
                  </div>
                </div>
              </div>

              {/* Foundation note */}
              <div className="ai-foundation-note">
                <p>
                  Every engagement includes the infrastructure work that makes
                  automation stick: <strong>AI governance policies</strong> your board
                  can approve, <strong>data architecture</strong> that keeps your
                  inputs clean, and <strong>CRM optimization</strong> so your pipeline
                  data actually means something.
                </p>
              </div>
            </div>

            {/* RIGHT COLUMN */}
            <div className="treasury-col-side">

              {/* Card 1: Our Approach */}
              <div className="side-card clarity-role-card">
                <h3>Our Approach</h3>
                <p className="clarity-role-intro" style={{ fontWeight: 600 }}>
                  We're lending operations people who know how to implement AI,
                  not the other way around.
                </p>
                <div className="clarity-role-list">
                  <div className="clarity-role-item">
                    <div className="clarity-role-bullet" style={{ color: '#1B4620' }}>&#9670;</div>
                    <div>
                      <strong>Operations First</strong>
                      <p>We map the process before we touch the tools. If the workflow is broken, automating it just makes it break faster.</p>
                    </div>
                  </div>
                  <div className="clarity-role-item">
                    <div className="clarity-role-bullet" style={{ color: '#1B4620' }}>&#9670;</div>
                    <div>
                      <strong>Human-in-the-Loop, Always</strong>
                      <p>AI handles the repetitive prep work. Your team owns the judgment, the relationships, and the decisions.</p>
                    </div>
                  </div>
                  <div className="clarity-role-item">
                    <div className="clarity-role-bullet" style={{ color: '#1B4620' }}>&#9670;</div>
                    <div>
                      <strong>Eyes Open on Risk</strong>
                      <p>AI can misclassify, hallucinate, and introduce bias. We build validation layers, audit trails, and human checkpoints into every workflow.</p>
                    </div>
                  </div>
                </div>
                <div className="ai-dont-footer">
                  <p>We don't sell software. We don't automate credit decisions. We don't replace your underwriters.</p>
                  <p className="ai-punchline">We make them faster.</p>
                </div>
              </div>

              {/* Card 2: Start Here */}
              <div className="side-card">
                <h3>Start Here</h3>
                <p className="clarity-role-intro">
                  Most CDFIs begin in one of two places:
                </p>
                <div className="clarity-role-list">
                  <div className="clarity-role-item">
                    <div className="clarity-role-bullet" style={{ color: '#1B4620' }}>&#9670;</div>
                    <div>
                      <strong>AI Governance Framework</strong>
                      <p>Policies your board and compliance team can approve: what tools are allowed, what data can flow where, who reviews outputs. Typically 4–6 weeks.</p>
                    </div>
                  </div>
                  <div className="clarity-role-item">
                    <div className="clarity-role-bullet" style={{ color: '#1B4620' }}>&#9670;</div>
                    <div>
                      <strong>Systems &amp; Readiness Assessment</strong>
                      <p>A structured review of your existing lending systems, data workflows, and team capacity to identify where AI can deliver the most value and what needs to be in place first.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="treasury-cta ai-cta">
          <div className="treasury-container">
            <h2>Let's Figure Out Where AI Fits</h2>
            <p>
              Whether you need a governance framework for your board or want to
              pilot automation on a specific workflow, we'll start with what
              matters most to your operation.
            </p>
            <Link to="/" state={{ scrollTo: 'contact' }} className="treasury-cta-btn">
              Get in Touch &rarr;
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default AIAutomation;
